import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { calculateServicePrice, parseHoursFromDuration } from '@/utils/priceCalculations';
import { useDurationOptions } from '@/hooks/useDurationOptions';
import { supabase } from '@/integrations/supabase/client';

export interface CartItem {
  id: string;
  title: string;
  price: number;
  image?: string;
  level?: string;
  offerId?: string;
  serviceId?: string;
  type: 'service' | 'offer' | 'event';
  personalizationNotes?: string;
  quantity?: number;
  priceUnit?: string; // Ajout du priceUnit pour afficher correctement l'unité de facturation
}

export interface QuantifiableOffer {
  offerId: string;
  title: string;
  quantity: number;
  basePrice: number;
  level: string;
  priceUnit?: string;
  totalPrice: number;
  billingType: 'quantifiable';
  notes?: string;
  // Ajout des informations de catégorie
  category?: string;
  categoryId?: string;
  categoryName?: string;
  categoryColor?: string;
}

interface CartContextType {
  items: CartItem[];
  quantifiableOffers: QuantifiableOffer[];
  itemCount: number;
  addItem: (item: CartItem) => Promise<void>;
  removeItem: (id: string) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  // Nouvelles méthodes pour les offres quantifiables
  addQuantifiableOffer: (offer: QuantifiableOffer) => void;
  updateOfferQuantity: (offerId: string, quantity: number, prestationDuration?: string) => void;
  removeQuantifiableOffer: (offerId: string) => void;
  getQuantifiableOffer: (offerId: string) => QuantifiableOffer | undefined;
  getAllQuantifiableOffers: () => QuantifiableOffer[];
  getOfferTotalPrice: (offerId: string, prestationDuration?: string) => number;
  getOfferQuantity: (offerId: string) => number;
  // Nouvelle méthode pour obtenir toutes les prestations pour le devis
  getAllCartItemsForQuote: () => Array<{
    type: 'standard' | 'quantifiable';
    title: string;
    level?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    billingType: string;
    notes?: string;
    offerId?: string;
    // Ajout des informations de catégorie et d'unité de prix
    category?: string;
    categoryId?: string;
    categoryName?: string;
    categoryColor?: string;
    priceUnit?: string;
  }>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [quantifiableOffers, setQuantifiableOffers] = useState<QuantifiableOffer[]>([]);
  const { durationOptions } = useDurationOptions();

  // Charger les données depuis localStorage au démarrage avec nettoyage
  useEffect(() => {
    const savedItems = localStorage.getItem('cart-items');
    const savedQuantifiableOffers = localStorage.getItem('cart-quantifiable-offers');
    
    console.log('🔄 Chargement du panier depuis localStorage...');
    
    if (savedItems) {
      try {
        const parsedItems = JSON.parse(savedItems);
        // Nettoyer les items malformés
        const cleanItems = parsedItems.filter((item: any) => 
          item && typeof item === 'object' && 
          typeof item.title === 'string' && 
          typeof item.price === 'number'
        );
        console.log('✅ Items propres chargés:', cleanItems);
        setItems(cleanItems);
      } catch (error) {
        console.error('❌ Erreur lors du chargement du panier, nettoyage:', error);
        localStorage.removeItem('cart-items');
        setItems([]);
      }
    }

    if (savedQuantifiableOffers) {
      try {
        const parsedOffers = JSON.parse(savedQuantifiableOffers);
        // Nettoyer les offres malformées et celles avec des objets _type/value
        const cleanOffers = parsedOffers.filter((offer: any) => 
          offer && 
          typeof offer === 'object' && 
          typeof offer.title === 'string' && 
          typeof offer.basePrice === 'number' &&
          typeof offer.quantity === 'number' &&
          offer.billingType === 'quantifiable' &&
          // Supprimer les objets avec des propriétés _type malformées
          (!offer.notes || typeof offer.notes === 'string') &&
          (!offer.category || typeof offer.category === 'string')
        ).map((offer: any) => {
          // Nettoyer complètement les objets _type malformés
          const cleanOffer = { ...offer };
          
          // Fonction pour nettoyer les propriétés _type
          const cleanProperty = (prop: any) => {
            if (prop && typeof prop === 'object' && prop._type === 'undefined') {
              return undefined;
            }
            return typeof prop === 'string' ? prop : undefined;
          };

          return {
            ...cleanOffer,
            notes: cleanProperty(offer.notes),
            category: cleanProperty(offer.category),
            categoryId: cleanProperty(offer.categoryId),
            categoryName: cleanProperty(offer.categoryName),
            categoryColor: cleanProperty(offer.categoryColor)
          };
        });
        
        console.log('✅ Offres quantifiables propres chargées:', cleanOffers);
        setQuantifiableOffers(cleanOffers);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des offres quantifiables, nettoyage:', error);
        localStorage.removeItem('cart-quantifiable-offers');
        setQuantifiableOffers([]);
      }
    }
  }, []);

  // Synchroniser automatiquement les priceUnit depuis la base de données
  const hasSyncedRef = useRef(false);
  
  useEffect(() => {
    const syncPriceUnitsFromDB = async () => {
      if (hasSyncedRef.current || (items.length === 0 && quantifiableOffers.length === 0)) return;
      
      console.log('🔄 Synchronisation des priceUnit depuis la base de données...');
      hasSyncedRef.current = true;
      
      try {
        const { data: services } = await supabase
          .from('services')
          .select('id, title, price_unit');
        
        const { data: offerServices } = await supabase
          .from('offer_services')
          .select('id, service_id, price_unit, service:services(id, title, price_unit)');
        
        if (!services && !offerServices) return;
        
        // Mettre à jour les items avec les bons priceUnit
        setItems(prev => prev.map(item => {
          const normalizedTitle = item.title.toLowerCase().trim();
          const matchingService = services?.find(s => 
            s.title.toLowerCase().trim() === normalizedTitle ||
            s.id === item.id ||
            s.id === item.serviceId
          );
          
          if (matchingService && matchingService.price_unit && matchingService.price_unit !== item.priceUnit) {
            console.log(`🔄 Sync priceUnit "${item.title}": ${item.priceUnit} → ${matchingService.price_unit}`);
            return { ...item, priceUnit: matchingService.price_unit };
          }
          return item;
        }));
        
        // Synchroniser les offres quantifiables
        setQuantifiableOffers(prev => prev.map(offer => {
          const matchingOfferService = offerServices?.find(os => {
            const serviceTitle = (os.service as any)?.title?.toLowerCase().trim();
            return serviceTitle === offer.title.toLowerCase().trim();
          });
          
          const correctPriceUnit = matchingOfferService?.price_unit || 
                                   (matchingOfferService?.service as any)?.price_unit;
          
          if (correctPriceUnit && correctPriceUnit !== offer.priceUnit) {
            console.log(`🔄 Sync priceUnit offre "${offer.title}": ${offer.priceUnit} → ${correctPriceUnit}`);
            return { ...offer, priceUnit: correctPriceUnit };
          }
          return offer;
        }));
        
        console.log('✅ Synchronisation des priceUnit terminée');
      } catch (error) {
        console.error('❌ Erreur lors de la synchronisation des priceUnit:', error);
      }
    };
    
    syncPriceUnitsFromDB();
  }, [items.length, quantifiableOffers.length]);

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    console.log('💾 Sauvegarde des items dans localStorage:', items);
    localStorage.setItem('cart-items', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    console.log('💾 Sauvegarde des offres quantifiables dans localStorage:', quantifiableOffers);
    localStorage.setItem('cart-quantifiable-offers', JSON.stringify(quantifiableOffers));
  }, [quantifiableOffers]);

  const addItem = async (item: CartItem) => {
    console.log('➕ Ajout d\'un item au panier:', item);
    
    setItems(prev => {
      const existingIndex = prev.findIndex(i => 
        i.id === item.id && 
        i.level === item.level && 
        i.offerId === item.offerId &&
        i.type === item.type
      );
      
      if (existingIndex >= 0) {
        console.log('⚠️ Item déjà présent dans le panier');
        return prev;
      }
      
      const newItems = [...prev, item];
      console.log('✅ Nouvel état du panier après ajout:', newItems);
      return newItems;
    });
  };

  const removeItem = (id: string) => {
    console.log('➖ Suppression d\'un item du panier:', id);
    setItems(prev => {
      const newItems = prev.filter(item => item.id !== id);
      console.log('✅ Nouvel état du panier après suppression:', newItems);
      return newItems;
    });
  };

  const clearCart = () => {
    console.log('🗑️ Vidage complet du panier');
    setItems([]);
    setQuantifiableOffers([]);
  };

  const isInCart = (id: string) => {
    const inCart = items.some(item => item.id === id);
    console.log(`🔍 L'item ${id} est-il dans le panier?`, inCart);
    return inCart;
  };

  // Nouvelles méthodes pour les offres quantifiables - CORRIGÉES pour éviter les doublons
  const addQuantifiableOffer = (offer: QuantifiableOffer) => {
    console.log('➕ Ajout/Remplacement d\'une offre quantifiable (un seul niveau par offre):', offer);
    setQuantifiableOffers(prev => {
      // Supprimer toute offre existante pour cette offre ID (pour éviter doublons)
      const filteredOffers = prev.filter(o => o.offerId !== offer.offerId);
      // Ajouter la nouvelle offre
      const newOffers = [...filteredOffers, offer];
      console.log('✅ Offres quantifiables après ajout/remplacement:', newOffers);
      return newOffers;
    });
  };

  const updateOfferQuantity = (offerId: string, quantity: number, prestationDuration?: string) => {
    console.log(`🔄 updateOfferQuantity appelée avec durée: "${prestationDuration}"`);
    
    setQuantifiableOffers(prev =>
      prev.map(offer => {
        if (offer.offerId === offerId) {
          console.log(`⚙️ Calcul pour ${offer.title} (${offer.priceUnit}) avec durée: ${prestationDuration}`);
          
          // Utiliser la fonction de calcul de prix pour prendre en compte la durée
          const priceCalculation = calculateServicePrice(
            offer.basePrice,
            offer.priceUnit || 'par_prestation',
            null, // nbGuests - pas utilisé pour les quantifiables
            prestationDuration,
            quantity,
            true, // isQuantifiable
            durationOptions
          );
          
          console.log(`💰 Résultat du calcul:`, priceCalculation);
          
          return { 
            ...offer, 
            quantity,
            totalPrice: priceCalculation.totalPrice
          };
        }
        return offer;
      })
    );
  };

  const removeQuantifiableOffer = (offerId: string) => {
    console.log('🗑️ Suppression offre quantifiable:', offerId);
    setQuantifiableOffers(prev => {
      const filtered = prev.filter(offer => offer.offerId !== offerId);
      console.log('✅ Offres quantifiables après suppression:', filtered);
      return filtered;
    });
  };

  const getQuantifiableOffer = (offerId: string) => {
    return quantifiableOffers.find(offer => offer.offerId === offerId);
  };

  const getAllQuantifiableOffers = () => {
    // Filtrer pour s'assurer qu'on ne retourne que les vrais offres quantifiables
    // (protection contre d'anciennes données corrompues dans localStorage)
    const validOffers = quantifiableOffers.filter(offer => 
      offer.billingType === 'quantifiable' && 
      offer.quantity && 
      offer.quantity > 0
    );
    
    // Si des offres ont été filtrées, nettoyer le localStorage
    if (validOffers.length !== quantifiableOffers.length) {
      console.log('🧹 Nettoyage des offres quantifiables invalides');
      setQuantifiableOffers(validOffers);
    }
    
    return validOffers;
  };

  const getOfferTotalPrice = (offerId: string, prestationDuration?: string) => {
    const offer = getQuantifiableOffer(offerId);
    if (!offer) return 0;
    
    // Si une durée est fournie, recalculer le prix avec la durée
    if (prestationDuration && offer.priceUnit === 'par_heure') {
      const priceCalculation = calculateServicePrice(
        offer.basePrice,
        offer.priceUnit,
        null,
        prestationDuration,
        offer.quantity,
        true,
        durationOptions
      );
      return priceCalculation.totalPrice;
    }
    
    return offer.totalPrice;
  };

  const getOfferQuantity = (offerId: string) => {
    const offer = getQuantifiableOffer(offerId);
    return offer ? offer.quantity : 0;
  };

  // Fonction pour normaliser les titres (même logique que dans les autres fichiers)
  const normalizeTitle = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/["""]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Nouvelle méthode pour consolider toutes les prestations pour le devis AVEC DÉDUPLICATION
  const getAllCartItemsForQuote = () => {
    const allItems: Array<{
      type: 'standard' | 'quantifiable';
      title: string;
      level?: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
      billingType: string;
      notes?: string;
      offerId?: string;
      // Ajout des informations de catégorie et d'unité de prix
      category?: string;
      categoryId?: string;
      categoryName?: string;
      categoryColor?: string;
      priceUnit?: string;
    }> = [];

    console.log('📋 Génération de la liste pour devis...');
    console.log('📦 Items standards:', items);
    console.log('📊 Offres quantifiables:', quantifiableOffers);

    // Ajouter les items standards (avec nettoyage des propriétés undefined)
    // NOTE: Pour les items standards avec priceUnit variable (par_personne, par_heure),
    // le prix réel sera recalculé lors de la génération du devis avec les paramètres de l'évènement
    items.forEach(item => {
      allItems.push({
        type: 'standard',
        title: item.title,
        level: item.level || undefined,
        quantity: item.quantity || 1,
        unitPrice: item.price,
        totalPrice: item.price * (item.quantity || 1),
        billingType: item.priceUnit || 'forfait',
        notes: typeof item.personalizationNotes === 'string' ? item.personalizationNotes : undefined,
        offerId: item.offerId || undefined,
        priceUnit: item.priceUnit || 'par_prestation' // Inclure le priceUnit depuis CartItem
      });
    });

    // CONVERSION EN FORMAT DEVIS avec quantités correctes
    const quantifiableMap = new Map<string, any>();
    
    quantifiableOffers.forEach(offer => {
      const title = offer.title || '';
      const level = offer.level || '';
      const priceUnit = offer.priceUnit || 'par_prestation';
      
      // Pour les quantifiables, utiliser uniquement titre + niveau comme clé unique
      const uniqueKey = `${normalizeTitle(title)}_${level.toLowerCase().trim()}`;
      
      console.log(`🔍 CartContext - Service quantifiable: "${title}", niveau: "${level}", priceUnit: "${priceUnit}"`);
      
      if (quantifiableMap.has(uniqueKey)) {
        // Si le service existe déjà, additionner les quantités
        const existing = quantifiableMap.get(uniqueKey);
        const existingQty = existing.quantity || 1;
        const currentQty = offer.quantity || 1;
        existing.quantity = existingQty + currentQty;
        existing.totalPrice = existing.unitPrice * existing.quantity;
        console.log(`🔄 CartContext - Service quantifiable dupliqué - quantités additionnées: "${title}" (${existingQty} + ${currentQty} = ${existing.quantity})`);
      } else {
        // Ajouter le nouveau service AVEC les bonnes propriétés pour l'email
        quantifiableMap.set(uniqueKey, {
          type: 'quantifiable',
          title: offer.title,
          name: offer.title, // Alias pour compatibilité
          level: offer.level,
          quantity: offer.quantity,
          unitPrice: offer.basePrice,
          price: offer.basePrice, // Alias pour compatibilité
          totalPrice: offer.totalPrice,
          billingType: 'quantifiable',
          priceUnit: priceUnit,
          price_unit: priceUnit, // Alias pour compatibilité
          notes: typeof offer.notes === 'string' ? offer.notes : undefined,
          offerId: offer.offerId,
          id: offer.offerId, // Alias pour compatibilité
          category: typeof offer.category === 'string' ? offer.category : undefined,
          categoryId: typeof offer.categoryId === 'string' ? offer.categoryId : undefined,
          categoryName: typeof offer.categoryName === 'string' ? offer.categoryName : undefined,
          categoryColor: typeof offer.categoryColor === 'string' ? offer.categoryColor : undefined
        });
        console.log(`✅ CartContext - Service quantifiable ajouté: "${title}" (niveau: ${level || 'N/A'}, quantité: ${offer.quantity}, priceUnit: ${priceUnit})`);
      }
    });

    // Ajouter les offres quantifiables dédupliquées
    Array.from(quantifiableMap.values()).forEach(offer => {
      allItems.push(offer);
    });

    console.log('✅ Liste consolidée pour devis (SANS DOUBLONS):', allItems);
    return allItems;
  };

  // CORRECTION: Utiliser useMemo pour que itemCount se recalcule automatiquement
  const itemCount = useMemo(() => {
    const count = items.length + quantifiableOffers.length;
    console.log('🔢 Nombre total d\'items dans le panier:', count);
    console.log('📦 Items standards:', items.length);
    console.log('📊 Offres quantifiables:', quantifiableOffers.length);
    return count;
  }, [items, quantifiableOffers]);

  const value: CartContextType = {
    items,
    quantifiableOffers,
    itemCount,
    addItem,
    removeItem,
    clearCart,
    isInCart,
    addQuantifiableOffer,
    updateOfferQuantity,
    removeQuantifiableOffer,
    getQuantifiableOffer,
    getAllQuantifiableOffers,
    getOfferTotalPrice,
    getOfferQuantity,
    getAllCartItemsForQuote
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
